import { Button } from '@customerio/pluma-components/react';
import { type Plan, Plans, type PlanType } from '@/services/billing/plans';
import type { OrdwayPlanBillingPlan } from '@/services/billing/transforms';
import { buildSearchString } from '@/utils/url';
import { BILLING_PATHS } from '../utils/paths';
import type { PlanInfoModel } from '../types';
import * as styles from './plan-info.css';

/** The external CDP early-access request form. */
const CDP_REQUEST_ACCESS_LINK = 'https://customer.io/cdp-early-access-request';

type CtaButtonProps = {
  plan: Plan;
  /** The matching Ordway plan, when loaded — supplies the `ordwayPlanID` for the checkout link. */
  ordwayPlan?: OrdwayPlanBillingPlan;
  model: PlanInfoModel;
  /** Opens the "Let's talk" sales sidebar. */
  onLetsTalk: (planType: PlanType) => void;
  /** Fires the `buy_or_request_access_clicked` analytics for any CTA, tagged with the button text. */
  onCtaClick: (text: string, planType: PlanType) => void;
};

/** The checkout href for the Buy-now CTA, carrying the Ordway plan id when one is available. */
function checkoutHref(ordwayPlan: OrdwayPlanBillingPlan | undefined): string {
  const search = ordwayPlan == null ? '' : buildSearchString({ ordwayPlanID: ordwayPlan.id });
  return search === '' ? BILLING_PATHS.checkout : `${BILLING_PATHS.checkout}?${search}`;
}

/**
 * Whether the plan shows a self-serve "Buy now" checkout button. Only the Essentials plan qualifies,
 * and never during a premium trial. Builder and CDP Starter accounts always qualify; otherwise the
 * account must not already have an active subscription.
 */
function shouldDisplayBuyNow(plan: Plan, model: PlanInfoModel): boolean {
  if (plan.planType !== Plans.Essentials.planType) {
    return false;
  }
  if (model.isPremiumTrial) {
    return false;
  }
  if (model.isBuilder) {
    return true;
  }
  if (model.currentPlanType === Plans.CdpStarter.planType) {
    return true;
  }
  return !model.hasActiveSubscription;
}

/** Whether this is a CDP plan, which shows a "Request access" link rather than a checkout/sales CTA. */
function isCdpContactForm(plan: Plan): boolean {
  return plan.planType === Plans.CdpStarter.planType || plan.planType === Plans.CdpPremium.planType;
}

/** The Pluma variant for the "Let's talk" button: primary for the recommended (non-CDP-Premium) plan. */
function letsTalkVariant(plan: Plan, isPrimary: boolean): 'primary' | 'secondary' {
  if (isPrimary && plan.planType !== Plans.CdpPremium.planType) {
    return 'primary';
  }
  return 'secondary';
}

/** The extra class for the "Let's talk" button: the refresh style for non-Essentials, else the secondary style. */
function letsTalkClassName(plan: Plan, variant: 'primary' | 'secondary'): string {
  if (plan.planType !== Plans.Essentials.planType) {
    return styles.ctaButtonRefresh;
  }
  if (variant === 'secondary') {
    return styles.ctaButtonSecondary;
  }
  return '';
}

/**
 * The primary call-to-action for a plan card: "Buy now" (self-serve checkout), "Request access" (CDP
 * plans), or "Let's talk" (sales sidebar). Every CTA fires the `buy_or_request_access_clicked`
 * analytics via {@link onCtaClick}, tagged with its button text; "Let's talk" also opens the sidebar.
 */
export function CtaButton({ plan, ordwayPlan, model, onLetsTalk, onCtaClick }: CtaButtonProps) {
  const isPrimary = plan.planType === model.nextUpgradePlanType;

  if (shouldDisplayBuyNow(plan, model)) {
    return (
      <Button
        data-test-plan-checkout-link
        href={checkoutHref(ordwayPlan)}
        variant="primary"
        isDisabled={model.isReadOnly}
        onClick={() => onCtaClick('Buy now', plan.planType)}
      >
        Buy now
      </Button>
    );
  }

  if (isCdpContactForm(plan)) {
    return (
      <Button
        data-test-plan-info-cdp-request-access
        href={CDP_REQUEST_ACCESS_LINK}
        isExternal={true}
        variant={isPrimary ? 'primary' : 'secondary'}
        className={isPrimary ? '' : styles.ctaButtonSecondary}
        onClick={() => onCtaClick('Request access', plan.planType)}
      >
        Request access
      </Button>
    );
  }

  const variant = letsTalkVariant(plan, isPrimary);
  return (
    <Button
      data-test-upsell-button={plan.planType}
      variant={variant}
      className={letsTalkClassName(plan, variant)}
      isDisabled={model.isReadOnly}
      onClick={() => {
        onCtaClick("Let's Talk", plan.planType);
        onLetsTalk(plan.planType);
      }}
    >
      Let's talk
    </Button>
  );
}
